// src/pages/DepositsPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { getCustomers } from '../api';
import { useNotification } from '../NotificationContext';

const DepositsPage = () => {
    const { addNotification } = useNotification();
    const location = useLocation();
    const navigate = useNavigate();

    // Data State
    const [deposits, setDeposits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_items: 0 });
    const [search, setSearch] = useState("");

    // Customer Selection State (for Create Modal)
    const [allCustomers, setAllCustomers] = useState([]);
    const [searchCustomerTerm, setSearchCustomerTerm] = useState("");

    // Modal State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);

    // Create Form State
    const [createForm, setCreateForm] = useState({
        customer_id: "",
        customer_name: "", // Only for display/locking if passed from CustomersPage
        amount: "",
        payment_method: "cash",
        note: ""
    });

    // Refund Form State
    const [refundForm, setRefundForm] = useState({
        deposit_id: null,
        amount_remaining: 0,
        refund_amount: "",
        reason: "",
        payment_method: "cash"
    });

    // Load data on mount or param change
    const loadDeposits = async (page = 1, searchQuery = "") => {
        setLoading(true);
        try {
            const res = await api.get('/deposits', {
                params: {
                    page,
                    per_page: 20,
                    search: searchQuery,
                    active_only: false // Show all history
                }
            });
            setDeposits(res.data.deposits || []);
            setPagination({
                page: res.data.current_page,
                total_pages: res.data.pages,
                total_items: res.data.total
            });
        } catch (err) {
            console.error("Load deposits error:", err);
            setDeposits([]);
            addNotification("โหลดข้อมูลไม่สำเร็จ: " + (err.response?.data?.message || err.message), "danger");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDeposits(1, search);
        // Check if passed from CustomersPage
        if (location.state?.prefillCustomer) {
            const cust = location.state.prefillCustomer;
            setCreateForm(prev => ({
                ...prev,
                customer_id: cust.id,
                customer_name: cust.name
            }));
            setShowCreateModal(true);
            // Clear state so it doesn't reopen on refresh? 
            // navigate(location.pathname, { replace: true });
        }
    }, []); // eslint-disable-line

    // Auto-search with debounce when search term changes
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            loadDeposits(1, search);
        }, 400); // 400ms delay
        return () => clearTimeout(debounceTimer);
    }, [search]); // eslint-disable-line

    // Load customers for selection when modal opens
    useEffect(() => {
        if (showCreateModal && allCustomers.length === 0) {
            getCustomers()
                .then(res => setAllCustomers(res.data || []))
                .catch(err => console.error("Failed to load customers for deposit selection", err));
        }
    }, [showCreateModal, allCustomers.length]);

    // Computed: Filtered Customers for Selection
    const filteredCustomers = allCustomers.filter(c => {
        if (!searchCustomerTerm) return true;
        const term = searchCustomerTerm.toLowerCase();
        return (
            c.name.toLowerCase().includes(term) ||
            (c.phone && c.phone.includes(term)) ||
            (c.member_id && c.member_id.toLowerCase().includes(term))
        );
    }).slice(0, 50);

    // Handlers
    const handleSearch = (e) => {
        e.preventDefault();
        loadDeposits(1, search);
    };

    const handleCreateSubmit = async () => {
        if (!createForm.customer_id || !createForm.amount || Number(createForm.amount) <= 0) {
            addNotification("กรุณาระบุข้อมูลให้ครบถ้วน", "warning");
            return;
        }

        try {
            await api.post('/deposits', {
                customer_id: createForm.customer_id,
                amount: createForm.amount,
                payment_method: createForm.payment_method,
                note: createForm.note
            });
            addNotification("บันทึกมัดจำเรียบร้อย", "success");
            setShowCreateModal(false);
            setCreateForm({ customer_id: "", customer_name: "", amount: "", payment_method: "cash", note: "" });
            loadDeposits(1, search); // reload
        } catch (err) {
            addNotification("บันทึกไม่สำเร็จ: " + (err.response?.data?.message || err.message), "danger");
        }
    };

    const openRefundModal = (dep) => {
        setRefundForm({
            deposit_id: dep.id,
            amount_remaining: dep.amount_remaining,
            refund_amount: "",
            reason: "",
            payment_method: "cash"
        });
        setShowRefundModal(true);
    };

    const handleRefundSubmit = async () => {
        if (!refundForm.refund_amount || Number(refundForm.refund_amount) <= 0 || Number(refundForm.refund_amount) > refundForm.amount_remaining) {
            addNotification("ยอดเงินคืนไม่ถูกต้อง", "warning");
            return;
        }

        if (!window.confirm("ยืนยันการคืนเงินมัดจำ?")) return;

        try {
            await api.post(`/deposits/${refundForm.deposit_id}/refund`, {
                amount: refundForm.refund_amount,
                reason: refundForm.reason,
                payment_method: refundForm.payment_method
            });
            addNotification("คืนเงินมัดจำเรียบร้อย", "success");
            setShowRefundModal(false);
            loadDeposits(pagination.page, search);
        } catch (err) {
            addNotification("คืนเงินไม่สำเร็จ: " + (err.response?.data?.message || err.message), "danger");
        }
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-hero mb-3">
                <div>
                    <div className="dashboard-hero-title">
                        <i className="bi bi-wallet2 me-2"></i>จัดการเงินมัดจำ (Deposits)
                    </div>
                    <div className="dashboard-hero-subtitle">
                        ดูประวัติ สร้างรายการ และคืนเงินมัดจำลูกค้า
                    </div>
                </div>
                {!location.state?.prefillCustomer && (
                    <button
                        className="btn btn-ks-primary"
                        style={{ color: '#ffffff' }}
                        onClick={() => {
                            setCreateForm({ customer_id: "", customer_name: "", amount: "", payment_method: "cash", note: "" });
                            setShowCreateModal(true);
                        }}
                    >
                        <i className="bi bi-plus-lg me-1"></i> รับมัดจำใหม่
                    </button>
                )}
            </div>

            {/* Search Bar */}
            <div className="card card-ks-soft mb-4">
                <div className="card-body">
                    <form onSubmit={handleSearch} className="d-flex gap-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="ค้นหาชื่อลูกค้า, เบอร์โทร, รหัสสมาชิก..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button type="submit" className="btn btn-outline-primary">
                            <i className="bi bi-search"></i> ค้นหา
                        </button>
                    </form>
                </div>
            </div>

            {/* Table */}
            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '50px' }}>ID</th>
                                <th>วันที่</th>
                                <th>ลูกค้า</th>
                                <th>ยอดมัดจำ</th>
                                <th>ช่องทาง</th>
                                <th>คงเหลือ</th>
                                <th>สถานะ</th>
                                <th>ผู้ทำรายการ</th>
                                <th>Note</th>
                                <th className="text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="10" className="text-center py-4">กำลังโหลด...</td></tr>
                            ) : deposits.length === 0 ? (
                                <tr><td colSpan="10" className="text-center py-4 text-muted">ไม่พบข้อมูล</td></tr>
                            ) : (
                                deposits.map((d) => (
                                    <tr key={d.id}>
                                        <td><span className="text-muted">#{d.id}</span></td>
                                        <td>{new Date(d.date || d.created_at).toLocaleDateString('th-TH')} {new Date(d.date || d.created_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}</td>
                                        <td>
                                            <div className="fw-bold">{d.customer_name} <span className="text-muted fw-normal">({d.member_id})</span></div>
                                            <div className="small text-muted">{d.customer_phone || "-"}</div>
                                        </td>
                                        <td>{Number(d.amount_initial).toLocaleString()}</td>
                                        <td>
                                            {d.payment_method === 'transfer' ? (
                                                <span className="badge bg-info text-dark"><i className="bi bi-bank me-1"></i>โอน</span>
                                            ) : (
                                                <span className="badge bg-secondary"><i className="bi bi-cash me-1"></i>สด</span>
                                            )}
                                        </td>
                                        <td className="fw-bold text-success">{Number(d.amount_remaining).toLocaleString()}</td>
                                        <td>
                                            <span className={`badge ${d.amount_remaining > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                                {d.amount_remaining > 0 ? 'Active' : 'Used/Refunded'}
                                            </span>
                                        </td>
                                        <td className="small">{d.created_by || '-'}</td>
                                        <td className="small text-muted" style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {d.note}
                                        </td>
                                        <td className="text-end">
                                            {d.amount_remaining > 0 && (
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => openRefundModal(d)}>
                                                    คืนเงิน
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>
                {/* Pagination */}
                <div className="card-footer d-flex justify-content-between align-items-center">
                    <span className="small text-muted">
                        ทั้งหมด {pagination.total_items} รายการ (หน้า {pagination.page}/{pagination.total_pages})
                    </span>
                    <nav>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => loadDeposits(pagination.page - 1, search)}>ก่อนหน้า</button>
                            </li>
                            <li className={`page-item ${pagination.page >= pagination.total_pages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => loadDeposits(pagination.page + 1, search)}>ถัดไป</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px' }}>
                        <div className="modal-content shadow">
                            <div className="modal-header">
                                <h5 className="modal-title">รับมัดจำใหม่</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
                            </div>
                            <div className="modal-body p-0">
                                {/* Step 1: Select Customer */}
                                {!createForm.customer_id ? (
                                    <div className="p-3">
                                        <h6 className="fw-bold mb-3">1. เลือกลูกค้า (VIP Member)</h6>
                                        <input
                                            type="text"
                                            className="form-control mb-3"
                                            placeholder="ค้นหาชื่อ, เบอร์โทร..."
                                            value={searchCustomerTerm}
                                            onChange={(e) => setSearchCustomerTerm(e.target.value)}
                                            autoFocus
                                        />
                                        <div className="list-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {filteredCustomers.length === 0 ? (
                                                <div className="text-muted text-center py-3">ไม่พบลูกค้า</div>
                                            ) : (
                                                filteredCustomers.map(c => (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                        onClick={() => {
                                                            setCreateForm(prev => ({ ...prev, customer_id: c.id, customer_name: c.name }));
                                                        }}
                                                    >
                                                        <div>
                                                            <div className="fw-bold">{c.name}</div>
                                                            <div className="small text-muted">{c.phone || "-"}</div>
                                                        </div>
                                                        <span className="badge bg-light text-dark">{c.member_id}</span>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    /* Step 2: Deposit Details */
                                    <div className="p-3">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="fw-bold mb-0">2. รายละเอียดมัดจำ</h6>
                                            <button
                                                className="btn btn-sm btn-link text-decoration-none"
                                                onClick={() => setCreateForm(prev => ({ ...prev, customer_id: "", customer_name: "" }))}
                                            >
                                                เปลี่ยนลูกค้า
                                            </button>
                                        </div>

                                        <div className="alert alert-success d-flex align-items-center py-2">
                                            <i className="bi bi-person-check-fill me-2 fs-5"></i>
                                            <div>
                                                <strong>{createForm.customer_name}</strong>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">ยอดเงินมัดจำ (บาท) <span className="text-danger">*</span></label>
                                            <input
                                                type="number"
                                                className="form-control form-control-lg fw-bold text-end text-primary"
                                                placeholder="0.00"
                                                value={createForm.amount}
                                                onChange={e => setCreateForm({ ...createForm, amount: e.target.value })}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">ช่องทางชำระ</label>
                                            <div className="btn-group w-100" role="group">
                                                <input
                                                    type="radio"
                                                    className="btn-check"
                                                    name="paymethod"
                                                    id="pm_cash"
                                                    autoComplete="off"
                                                    checked={createForm.payment_method === "cash"}
                                                    onChange={() => setCreateForm({ ...createForm, payment_method: "cash" })}
                                                />
                                                <label className="btn btn-outline-secondary" htmlFor="pm_cash">
                                                    <i className="bi bi-cash me-1"></i>เงินสด
                                                </label>

                                                <input
                                                    type="radio"
                                                    className="btn-check"
                                                    name="paymethod"
                                                    id="pm_transfer"
                                                    autoComplete="off"
                                                    checked={createForm.payment_method === "transfer"}
                                                    onChange={() => setCreateForm({ ...createForm, payment_method: "transfer" })}
                                                />
                                                <label className="btn btn-outline-secondary" htmlFor="pm_transfer">
                                                    <i className="bi bi-bank me-1"></i>โอนเงิน
                                                </label>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">หมายเหตุ</label>
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                value={createForm.note}
                                                onChange={e => setCreateForm({ ...createForm, note: e.target.value })}
                                                placeholder="เช่น มัดจำค่า..."
                                            ></textarea>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>ยกเลิก</button>
                                {createForm.customer_id && (
                                    <button type="button" className="btn btn-primary px-4" onClick={handleCreateSubmit}>
                                        <i className="bi bi-save me-1"></i> บันทึกมัดจำ
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Refund Modal */}
            {showRefundModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px' }}>
                        <div className="modal-content shadow">
                            <div className="modal-header">
                                <h5 className="modal-title text-danger">คืนเงินมัดจำ</h5>
                                <button type="button" className="btn-close" onClick={() => setShowRefundModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-secondary">
                                    ยอดคงเหลือที่คืนได้: <strong>{Number(refundForm.amount_remaining).toLocaleString()} ฿</strong>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">ยอดคืน (บาท)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        max={refundForm.amount_remaining}
                                        value={refundForm.refund_amount}
                                        onChange={e => setRefundForm({ ...refundForm, refund_amount: e.target.value })}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">คืนโดย</label>
                                    <select
                                        className="form-select"
                                        value={refundForm.payment_method}
                                        onChange={e => setRefundForm({ ...refundForm, payment_method: e.target.value })}
                                    >
                                        <option value="cash">เงินสด</option>
                                        <option value="transfer">โอนเงิน</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">สาเหตุ</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={refundForm.reason}
                                        onChange={e => setRefundForm({ ...refundForm, reason: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowRefundModal(false)}>ยกเลิก</button>
                                <button type="button" className="btn btn-danger" onClick={handleRefundSubmit}>ยืนยันคืนเงิน</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DepositsPage;
